/* ── ResourceModal ──────────────────────────────────────────────────── */
class ResourceModal extends HTMLElement {
  constructor() {
    super();

    this.selectors = {
      overlay: '.resource-modal__overlay',
      dialog:  '.resource-modal__dialog',
      close:   '.resource-modal__close',
      form:    '.contact-form--hb',
      error:   '.resource-modal__error',
    };

    this._downloadUrl  = null;
    this._submitting   = false;
    this._successHandled = false;
    this._gen          = 0;
    this._returnFocus  = null;

    // All focusable element types for focus-trap.
    this._focusableSelectors = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');

    this.init();
  }

  init() {
    // Delegate clicks on buttons that target this modal by ID.
    this._triggerHandler = (e) => {
      const btn = e.target.closest(`[data-resource-modal-target="${this.id}"]`);
      if (!btn) return;
      e.preventDefault();

      const downloadUrl =
        btn.dataset.downloadUrl ||
        btn.getAttribute('data-download-url') ||
        btn.getAttribute('download-url') ||
        '';

      // If user already submitted once in this page session, skip the form.
      if (this._hasSubmittedMessage()) {
        this._downloadUrl = downloadUrl || null;
        this._triggerDownload();
        return;
      }

      this.open(downloadUrl);
    };
    document.addEventListener('click', this._triggerHandler);

    // Close button.
    this.querySelector(this.selectors.close)
      ?.addEventListener('click', () => this.close());

    // Click on overlay backdrop (not dialog) to close.
    this.querySelector(this.selectors.overlay)
      ?.addEventListener('click', (e) => {
        if (e.target === e.currentTarget) this.close();
      });

    // Keyboard: Escape to close, Tab to trap focus.
    this._keyHandler = (e) => {
      if (!this.classList.contains('is-open')) return;
      if (e.key === 'Escape') { this.close(); return; }
      if (e.key === 'Tab') this._trapFocus(e);
    };
    document.addEventListener('keydown', this._keyHandler);

    // Wire up the Salesforce form to submit through a hidden iframe
    // so we can detect the redirect and trigger the PDF download.
    this._wireForm();

    // HubSpot form replaces wrapper content with `.submitted-message` on success.
    // Watch for that state and treat it as successful submission.
    this._watchHubspotSuccess();
  }

  _watchHubspotSuccess() {
    const wrapper = this.querySelector('#hubspot-form-wrapper');
    if (!wrapper) return;

    this._successObserver = new MutationObserver(() => {
      if (!this.classList.contains('is-open')) return;
      if (wrapper.querySelector('.submitted-message')) {
        this._onSuccess();
      }
    });

    this._successObserver.observe(wrapper, { childList: true, subtree: true });
  }

  _hasSubmittedMessage() {
    const wrapper = this.querySelector('#hubspot-form-wrapper');
    return Boolean(wrapper?.querySelector('.submitted-message'));
  }

  _triggerDownload() {
    if (!this._downloadUrl) return;

    try {
      const anchor = document.createElement('a');
      anchor.href = this._downloadUrl;
      anchor.download = '';
      anchor.target = '_blank';
      anchor.rel = 'noopener noreferrer';
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
    } catch (_) {
      // Fallback: open in new tab.
      window.open(this._downloadUrl, '_blank', 'noopener,noreferrer');
    }
  }

  _wireForm() {
    const form = this.querySelector(this.selectors.form);
    if (!form) return;

    // Create or reuse the hidden iframe that acts as the form target.
    const iframeName = `resource-modal-iframe-${this.id}`;
    let iframe = this.querySelector(`#${iframeName}`);
    if (!iframe) {
      iframe = document.createElement('iframe');
      iframe.id   = iframeName;
      iframe.name = iframeName;
      iframe.setAttribute('hidden', '');
      iframe.setAttribute('aria-hidden', 'true');
      this.appendChild(iframe);
    }

    // Point the form at the iframe so submission doesn't navigate the page.
    form.target = iframeName;

    form.addEventListener('submit', () => {
      // Guard against double-submission.
      if (this._submitting) return;
      this._submitting = true;
      this.classList.remove('is-error');

      const submitBtn = form.querySelector('[type="submit"]');
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.classList.add('is-loading');
      }
    });

    // The iframe fires `load` once on initial blank load, then again after
    // the form's redirect lands. We use _submitting as a gate so the first
    // blank load is ignored.
    iframe.addEventListener('load', () => {
      if (!this._submitting) return;
      this._onSuccess();
    });
  }

  _onSuccess() {
    if (this._successHandled) return;
    this._successHandled = true;
    this._submitting = false;
    this.classList.add('is-success');

    // Trigger the PDF download.
    this._triggerDownload();

    // Auto-close after 2.5 s so the user sees the success message.
    setTimeout(() => this.close(), 2500);
  }

  open(downloadUrl) {
    this._downloadUrl = downloadUrl || null;
    this._successHandled = false;
    this._gen++;

    // Reset any previous state.
    this.classList.remove('is-closing', 'is-success', 'is-error');
    this._resetForm();

    this.classList.add('is-open');
    document.body.classList.add('resource-modal-open');

    // Store the element that had focus so we can restore it on close.
    this._returnFocus = document.activeElement;

    // Move focus into the dialog for accessibility.
    this.querySelector(this.selectors.close)?.focus();
  }

  close() {
    const closedGen = this._gen;
    this.classList.add('is-closing');

    const dialog  = this.querySelector(this.selectors.dialog);
    const finish  = () => {
      if (this._gen !== closedGen) return; // open() called before animation ended
      this.classList.remove('is-open', 'is-closing', 'is-success', 'is-error');
      document.body.classList.remove('resource-modal-open');
      this._returnFocus?.focus();
    };

    if (dialog) {
      dialog.addEventListener('animationend', finish, { once: true });
    } else {
      finish();
    }
  }

  _resetForm() {
    const form = this.querySelector(this.selectors.form);
    if (form) form.reset();
    this._submitting = false;
    this._successHandled = false;
    const submitBtn = form?.querySelector('[type="submit"]');
    if (submitBtn) {
      submitBtn.disabled = false;
      submitBtn.classList.remove('is-loading');
    }
    const errorEl = this.querySelector(this.selectors.error);
    if (errorEl) errorEl.textContent = '';
  }

  _trapFocus(e) {
    const dialog = this.querySelector(this.selectors.dialog);
    if (!dialog) return;

    const focusable = [...dialog.querySelectorAll(this._focusableSelectors)].filter(
      (el) => !el.closest('[hidden]') && el.offsetParent !== null
    );
    if (!focusable.length) return;

    const first = focusable[0];
    const last  = focusable[focusable.length - 1];

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  disconnectedCallback() {
    this._successObserver?.disconnect();
    document.removeEventListener('click', this._triggerHandler);
    document.removeEventListener('keydown', this._keyHandler);
  }
}

customElements.define('resource-modal', ResourceModal);
